import {Types} from "@Common/types";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {parseUrlQuery}  from "@Common/utils/parse-url-query";

interface GetUsersApiParams {
    idBranch?:string,
    search?:string,
    limit?:number,
    offset?:number,
    inactive?:boolean,
}

interface GetUsersApiItem {
    agentDisplay1: string,
    agentDisplay2: string,
    agentDisplay3: string,
    agentDisplay4: string,
    agentDisplay5: string,
    agentDisplay6: string,
    agentDisplay7: string,
    agentDisplay8: string,
    agentEmail: string,
    agentLinkId: string,
    agentNameFirst: string,
    agentNameLast: string,
    agentNameMiddle: string,
    idBranch : string,
    branchName: string,
    agentPhone1: string,
    agentPhone2: string,
    idAgent: string,
    idUser: number,
    headshot: string,
    loginLevel: Types.Apis.Models.UserLevel,
    agentActive: boolean,
    userActive: boolean,
};

type GetUsersApiResult = GetUsersApiItem[];

export const UserManagementController = ({next}: {next:(v:Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerNext) => any}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("routerHistory"),
    ["office", ({}, next) => next(parseUrlQuery(window.location.search)?.office)],
    ["state", ({office}, next) => UseState({next, initValue: {
        idBranch          : office ?? "",
        colCount          : 3,
        done              : false,
        fetching          : false,
        includingInactive : false,
        search            : "",
        users             : [],
    }})],

    ["fetchRecordsIntoState", (
        {apis, state}:{
            apis  : any,
            state : Types.Common.React.UseState.UseStateNext<Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState>
        },
        next:(v:any) => any
    ) => next(makeRecordsFetcher(apis.next.agent.getAllUsers, state.onChange))],

    ({company, fetchRecordsIntoState, routerHistory, state}:{
        company                 : Types.Xpresso.Loaders.ContextNameMap['company'],
        fetchRecordsIntoState   : ReturnType<typeof makeRecordsFetcher>,
        routerHistory           : Types.Xpresso.Loaders.ContextNameMap['routerHistory'],
        state                   : Types.Common.React.UseState.UseStateNext<Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState>,
    }) => next({
        ...state.value,

        handleColCountChange: (colCount: number) =>
        {
            state.onChange({...state.value, colCount});

            if (!state.value.done && !state.value.fetching) {
                const limit = state.value.users.length === 0 ? mapStateAsBatchLimit(state.value) : mapStateAsCellsMissing(state.value);
                fetchRecordsIntoState(limit, state.value);
            }
        },

        handleIncludeInactiveSwitchRequest: async (includeInactive:boolean) =>
        {
            const newStateValue = {
                ...state.value,
                includingInactive: includeInactive,
                done: false,
                fetching: true,
                users: new Array
            };
            await new Promise(resolve => state.onChange(newStateValue, resolve as () => void));
            fetchRecordsIntoState(mapStateAsBatchLimit(newStateValue), newStateValue);
        },

        handleLoadMoreRequest: () =>
        {
            if (!state.value.done && !state.value.fetching)
                fetchRecordsIntoState(mapStateAsBatchLimit(state.value), state.value);
        },

        handleSearchChangeRequest: async (search:string) =>
        {
            const newStateValue = {
                ...state.value,
                search,
                done: false,
                fetching: true,
                users: new Array
            };
            await new Promise(resolve => state.onChange(newStateValue, resolve as () => void));
            fetchRecordsIntoState(mapStateAsBatchLimit(newStateValue), newStateValue);
        },

        handleBranchChangeRequest: async (idBranch:string) =>
        {
            const newStateValue = {
                ...state.value,
                idBranch,
                done: false,
                fetching: true,
                users: new Array
            };
            await new Promise(resolve => state.onChange(newStateValue, resolve as () => void));
            fetchRecordsIntoState(mapStateAsBatchLimit(newStateValue), newStateValue);
        },

        handleUserCreateRequest: company?.prefs.signupCreateAgent && (() => routerHistory.replace('/account/admin/user?modal=create&step=general')),

        reload: async () =>
        {
            const currentLength = state.value.users.length;
            const bottomOfResults = state.value.done;
            const newStateValue = {
                ...state.value,
                fetching: true,
                users: new Array
            };
            await new Promise(resolve => state.onChange(newStateValue, resolve as () => void));
            fetchRecordsIntoState(currentLength, newStateValue, bottomOfResults);
        },
    }),
);

const makeRecordsFetcher = (getUsersApi:(params:GetUsersApiParams) => Promise<GetUsersApiResult>, setState: (value:Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState) => void) => {
    return async (limit:number, stateValue:Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState, forceDone?:boolean) => {
        if (limit===0) {
            setState({...stateValue, fetching: false});
            return;
        }

        setState({...stateValue, fetching: true});

        const
            params     = makeApiParams(limit, stateValue),
            apiResults = await getUsersApi(params),
            apiUsers   = mapApiResultsAsUsers(apiResults),
            users      = stateValue.users.concat(apiUsers),
            done       = forceDone || apiUsers.length < limit
        ;

        setState({...stateValue, done, users, fetching: false});
    };
}

const makeApiParams = (limit:number, stateValue:Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState) =>
{
    return {
        idBranch: stateValue.idBranch,
        limit,
        inactive: stateValue.includingInactive,
        offset: stateValue.users.length,
        search: stateValue.search,
    };
}

const mapApiResultsAsUsers = function(results:GetUsersApiResult): Types.Xpresso.Account.Admin.UserManagement.UserData[]
{
    return results.map(({idUser, idAgent, agentNameFirst, agentNameMiddle, agentNameLast, agentEmail, agentLinkId, idBranch, branchName, headshot, loginLevel, agentActive, userActive}) => {
        return {
            idUser,
            idAgent,
            headshot,
            loginLevel,
            userActive,
            agentActive,
            agentNameFirst,
            agentNameMiddle,
            agentNameLast,
            agentEmail,
            agentLinkId,
            idBranch,
            branchName,
            fullName: [agentNameFirst, agentNameMiddle, agentNameLast].filter(Boolean).join(" "),
        };
    });
}

const mapStateAsBatchLimit = (stateValue:Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState): number => {
    return stateValue.colCount * 3 + mapStateAsCellsMissing(stateValue);
}

const mapStateAsCellsMissing = (stateValue:Types.Xpresso.Account.Admin.UserManagement.UserManagementControllerState): number => {
    const
        cellsTaken = stateValue.users.length + 1,
        remainder  = cellsTaken % stateValue.colCount
    ;
    return (stateValue.colCount - remainder) % stateValue.colCount;
};
